<template>
  <Authenticated>
    <div class="wrapper">
      <section v-if="Object.keys(customerDetails).length === 0" class="pay-options">
        <div class="content">
          <div class="subtitle">
            <h4>Customer Information</h4>
          </div>
          <p>Business Name: {{ companySettings.company_name }}</p>
          <label for="email">Email Address *</label>
          <input id="email" type="email" v-model="customerForm.email" placeholder="e.g. user@example.com" required />

          <label for="country">Country *</label>
          <select id="country" v-model="customerForm.address.country" required>
            <option value="">Select Country</option>
            <option value="GB">United Kingdom</option>
            <option value="AU">Australia</option>
            <option value="NZ">New Zealand</option>
          </select>
          <label for="phone">Phone Number</label>
          <div class="phone-input-row">
            <div class="area-code">
              <input id="area-code" type="text" v-model="customerForm.phoneAreaCode" :placeholder="areaCodePlaceholder" readonly class="area-code-input" />
            </div>
            <div class="phone-number">
              <input id="phone" type="tel" v-model="customerForm.phoneNumber" :placeholder="phoneNumberPlaceholder" class="phone-number-input" />
            </div>
          </div>
        </div>

        <div class="content">
          <div class="subtitle">
            <h4>Billing Address</h4>
          </div>
          <label for="line1">Address Line 1 *</label>
          <input id="line1" type="text" v-model="customerForm.address.line1" placeholder="e.g. 123 Main Street" required />
          <label for="line2">Address Line 2</label>
          <input id="line2" type="text" v-model="customerForm.address.line2" placeholder="e.g. Apt 4B, Suite 200" />

          <label for="city">City *</label>
          <input id="city" type="text" v-model="customerForm.address.city" placeholder="e.g. Auckland" required />

          <label for="state">State/Province *</label>
          <input id="state" type="text" v-model="customerForm.address.state" placeholder="e.g. NY" required />

          <label for="postal_code">Postal Code *</label>
          <input id="postal_code" type="text" v-model="customerForm.address.postal_code" placeholder="e.g. 10001" required />
        </div>

        <div class="content">
          <button @click.stop.prevent="subscribe">Continue</button>
        </div>
      </section>
      <div v-if="confirmSubscription.status" class="success-banner">Subscription confirmed successfully. Thank you! It can take some time for the changes to reflect.</div>
      <section class="sections-container" v-if="!loading">
        <section class="details" v-if="hasSubscription">
          <header>
            <h2>Your subscription</h2>
            <button v-if="!customerDetails.subscriptions.data[0].cancel_at_period_end" class="danger" @click="handleConfirm">Cancel Subscription</button>
            <div class="reactivate" v-else>
              <span>Subscription Cancelling at the end of the current period</span>
              <button @click="reactivateSub">Reactivate Subscription</button>
            </div>
          </header>
          <section class="status">
            <div v-for="sub in customerDetails.subscriptions.data" :key="sub.id" class="subscription-card">
              <div class="subscription-header">
                <h3>Subscription Status</h3>
                <span :class="['status-badge', getStatusClass(sub.status)]">{{ sub.status.toUpperCase() }}</span>
              </div>

              <div class="subscription-info">
                <div class="info-row">
                  <span class="label">Created:</span>
                  <span class="value">{{ formatDate(sub.created) }}</span>
                </div>

                <div class="info-row">
                  <span class="label">Current Period:</span>
                  <span class="value">{{ formatDate(sub.current_period_start) }} - {{ formatDate(sub.current_period_end) }}</span>
                </div>

                <div class="info-row">
                  <span class="label">Next Billing:</span>
                  <span class="value">{{ formatDate(sub.current_period_end) }}</span>
                </div>
              </div>

              <div class="subscription-items">
                <h4>Subscription Items:</h4>
                <div v-for="item in sub.items.data" :key="item.id" class="item-row">
                  <div class="item-details">
                    <span class="item-name">{{ item.price.nickname }} </span>
                    <span class="item-quantity"> Qty: {{ item.quantity }}</span>
                  </div>
                  <div class="item-price">
                    ${{ (item.price.unit_amount / 100).toFixed(2) }} {{ item.price.currency.toUpperCase() }}
                    <span class="billing-interval">/ {{ item.price.recurring.interval }}</span>
                  </div>
                </div>
              </div>

              <div class="subscription-total">
                <strong>Total: ${{ calculateSubscriptionTotal(sub) }} {{ sub.currency.toUpperCase() }} / month</strong>
                <span class="why" @click="handleProrationInfo('why')">Why is this different to my invoice?</span>
              </div>
            </div>

            <div class="info-container">
              <div class="customer-info">
                <h3>Customer Information</h3>
                <div class="info-row">
                  <span class="label">Email: </span>
                  <span class="value">{{ customerDetails.email }}</span>
                </div>
                <div class="info-row" v-if="customerDetails.name">
                  <span class="label">Name: </span>
                  <span class="value">{{ customerDetails.name }}</span>
                </div>
                <div class="info-row" v-if="customerDetails.phone">
                  <span class="label">Phone: </span>
                  <span class="value">{{ customerDetails.phone }}</span>
                </div>
              </div>
              <div class="payment-methods">
                <h3>Payment Method</h3>
                <div v-if="customerDetails.subscriptions.data[0].default_payment_method" class="payment-card">
                  <div class="card-info">
                    <div class="card-brand">
                      <span class="brand-name">{{ customerDetails.subscriptions.data[0].default_payment_method.card.brand.toUpperCase() }}</span>
                      <span class="card-type">{{ customerDetails.subscriptions.data[0].default_payment_method.card.funding.toUpperCase() }}</span>
                    </div>
                    <div class="card-number">
                      <span>Ending in {{ customerDetails.subscriptions.data[0].default_payment_method.card.last4 }}</span>
                    </div>
                    <div class="card-details">
                      <span class="expiry"
                        >Expires {{ customerDetails.subscriptions.data[0].default_payment_method.card.exp_month }}/{{
                          customerDetails.subscriptions.data[0].default_payment_method.card.exp_year
                        }}</span
                      >
                      <span class="country">{{ customerDetails.subscriptions.data[0].default_payment_method.card.country }}</span>
                    </div>
                  </div>
                  <div class="default-badge">
                    <span>DEFAULT</span>
                  </div>
                </div>
                <div v-else class="no-payment-methods">
                  <p>No payment methods available.</p>
                </div>
                <button class="action" @click="showModal = true">Add Payment Method</button>
              </div>
            </div>
          </section>
          <header>
            <h2>Upcoming Invoice</h2>
            <span class="how" @click="handleProrationInfo('how')">How is my invoice calculated?</span>
          </header>
          <section class="invoice-container" v-if="hasSubscription">
            <div class="upcoming-invoice" v-if="upcomingInvoice">
              <div class="invoice-totals">
                <div class="total-row" v-if="upcomingInvoice.subtotal && (upcomingInvoice.tax || upcomingInvoice.discount)">
                  <span class="label">Subtotal:</span>
                  <span class="value">${{ (upcomingInvoice.subtotal / 100).toFixed(2) }} {{ upcomingInvoice.currency.toUpperCase() }}</span>
                </div>

                <div class="total-row" v-if="upcomingInvoice.tax">
                  <span class="label">Tax:</span>
                  <span class="value">${{ (upcomingInvoice.tax / 100).toFixed(2) }} {{ upcomingInvoice.currency.toUpperCase() }}</span>
                </div>

                <div class="total-row" v-if="upcomingInvoice.discount">
                  <span class="label">Discount:</span>
                  <span class="value" v-if="upcomingInvoice.discount.coupon.amount_off"
                    >-${{ upcomingInvoice.discount.coupon.amount_off }} {{ upcomingInvoice.currency.toUpperCase() }}</span
                  >
                  <span class="value" v-if="upcomingInvoice.discount.coupon.percent_off">{{ upcomingInvoice.discount.coupon.percent_off }}%</span>
                </div>

                <div class="total-row final-total">
                  <span class="label">Total:</span>
                  <span class="value">${{ (upcomingInvoice.total / 100).toFixed(2) }} {{ upcomingInvoice.currency.toUpperCase() }}</span>
                </div>

                <div class="total-row" v-if="upcomingInvoice.amount_due !== upcomingInvoice.total">
                  <span class="label">Amount Due:</span>
                  <span class="value">${{ (upcomingInvoice.amount_due / 100).toFixed(2) }} {{ upcomingInvoice.currency.toUpperCase() }}</span>
                </div>
              </div>
              <div class="invoice-items">
                <h4>Invoice Items:</h4>
                <div v-for="item in upcomingInvoice.lines.data" :key="item.id" class="invoice-item">
                  <div class="invoice-detail">
                    <span>{{ item.description || item.price.nickname || "Service" }}</span>
                    <p v-if="item.period">{{ formatDate(item.period.start) }} - {{ formatDate(item.period.end) }}</p>
                  </div>
                  <div class="invoice-detail">
                    <span v-if="item.quantity > 1">{{ item.quantity }} × ${{ (item.price.unit_amount / 100).toFixed(2) }}</span>
                    <p>${{ (item.amount / 100).toFixed(2) }} {{ upcomingInvoice.currency.toUpperCase() }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="no-invoice">
              <p>No upcoming invoice available</p>
            </div>
          </section>
        </section>
      </section>
      <div v-if="error.status" class="error">
        {{ error.details }}
      </div>
    </div>
    <ProrationInfo @close="showProrationInfo = false" :isOpen="showProrationInfo" :scrollTo="prorationScrollTo" />
    <PaymentMethodModal :open="showModal" :company="companyName" @payment-method-created="addPaymentMethod" @close="showModal = false" />
    <Confirm :open="showConfirm" :message="confirmMessage" :isDeleteConfirm="true" @confirmModal="cancelSubscription" @close="showConfirm = false" />
    <div class="loading" v-if="loading">
      <LoadingSpinner :spinnerMessage="'Processing...'" />
    </div>
  </Authenticated>
</template>

<script>
import Authenticated from "@/components/_layouts/Authenticated.vue";
import { mapGetters } from "vuex";
import { api } from "@monolith/helpers";
import { nanoid } from "nanoid";
import PaymentMethodModal from "./PaymentMethodModal.vue";
import { getStripe } from "./stripe.js";
import { Confirm } from "@monolith/components";
import { LoadingSpinner } from "@monolith/components";
import ProrationInfo from "./ProrationInfo.vue";

export default {
  name: "StripeDash",
  components: { Authenticated, PaymentMethodModal, Confirm, LoadingSpinner, ProrationInfo },
  data() {
    return {
      loading: false,
      confirmMessage: "",
      showConfirm: false,
      stripe: null,
      subSecret: null,
      tenantId: null,
      showModal: false,
      showProrationInfo: false,
      prorationScrollTo: "",
      customerForm: {
        email: "",
        name: "",
        phoneAreaCode: "",
        phoneNumber: "",
        address: {
          country: "",
          city: "",
          line1: "",
          line2: "",
          postal_code: "",
          state: "",
        },
      },
      countryCodes: {
        GB: "+44",
        AU: "+61",
        NZ: "+64",
      },
      confirmSubscription: {
        details: null,
        status: false,
      },
      error: {
        details: null,
        status: false,
      },
    };
  },

  async mounted() {
    await this.initializeStripe();
    if (!this.companySettings.stripe_subscribed) return;
    // Only fetch if getAllData didn't already populate customer details
    if (!this.customerDetails || !this.customerDetails.id) {
      this.loading = true;
      await this.$store.dispatch("getStripeCustomerDetails");
      if (this.customerDetails && this.customerDetails.id) {
        await this.$store.dispatch("getUpcomingStripeInvoice");
      }
      this.loading = false;
    }
  },

  methods: {
    async reactivateSub() {
      this.loading = true;
      let result = await api.reactivate_stripe_subscription(this.customerDetails.subscriptions.data[0].id);
      if (result.error) {
        this.error.status = true;
        this.error.details = result.error.message;
        this.loading = false;
        return;
      }
      this.error.status = false;
      this.error.details = null;
      await this.$store.dispatch("getStripeCustomerDetails");
      await this.$store.dispatch("getUpcomingStripeInvoice");
      this.loading = false;
    },

    async addPaymentMethod(result) {
      this.showModal = false;
      this.loading = true;

      let subscriptions = this.customerDetails && this.customerDetails.subscriptions;
      let subscriptionData = subscriptions && subscriptions.data;
      let hasExistingSubscription = subscriptionData && subscriptionData.length > 0;

      if (hasExistingSubscription) {
        await api.add_stripe_payment_method(result, this.customerDetails.id, this.customerDetails.subscriptions.data[0].id);
        await this.$store.dispatch("getStripeCustomerDetails");
        await this.$store.dispatch("getUpcomingStripeInvoice");
        this.loading = false;
      } else {
        await this.createSubscription(result);
      }
    },

    handleProrationInfo(section) {
      this.prorationScrollTo = section;
      this.showProrationInfo = true;
    },

    async cancelSubscription() {
      this.showConfirm = false;
      this.loading = true;
      const subscriptionId = this.customerDetails.subscriptions.data[0].id;
      let result = await api.cancel_stripe_subscription(subscriptionId);
      if (result.error) {
        this.error.status = true;
        this.error.details = result.error.message;
        this.loading = false;
        return;
      } else {
        await this.$store.dispatch("getStripeCustomerDetails");
      }
      this.loading = false;
    },

    handleConfirm() {
      this.confirmMessage = "Are you sure you want to cancel your subscription";
      this.showConfirm = true;
    },

    async initializeStripe() {
      this.stripe = await getStripe();
      if (!this.stripe) {
        this.error.status = true;
        this.error.details = "Failed to load payment system. Please refresh and try again.";
      }
    },

    // Helper methods for subscription display
    formatDate(timestamp) {
      if (!timestamp) return "N/A";
      return new Date(timestamp * 1000).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    calculateSubscriptionTotal(subscription) {
      if (!subscription.items || !subscription.items.data) return "0.00";

      const total = subscription.items.data.reduce((sum, item) => {
        return sum + item.price.unit_amount * item.quantity;
      }, 0);

      return (total / 100).toFixed(2);
    },

    async subscribe() {
      if (
        !this.customerForm.email ||
        !this.customerForm.address.line1 ||
        !this.customerForm.address.city ||
        !this.customerForm.address.state ||
        !this.customerForm.address.postal_code ||
        !this.customerForm.address.country
      ) {
        this.error.status = true;
        this.error.details = "Please fill in all required fields (marked with *)";
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.customerForm.email)) {
        this.error.status = true;
        this.error.details = "Please enter a valid email address";
        return;
      }

      this.loading = true;
      try {
        if (!this.tenantId) this.tenantId = nanoid();
        const customerData = {
          email: this.customerForm.email,
          name: this.companyName,
          phone: this.combinedPhoneNumber || null,
          address: {
            country: this.customerForm.address.country,
            city: this.customerForm.address.city,
            line1: this.customerForm.address.line1,
            line2: this.customerForm.address.line2 || null,
            postal_code: this.customerForm.address.postal_code,
            state: this.customerForm.address.state,
          },
          metadata: {
            tenant_id: this.tenantId,
            company: this.companyName,
          },
        };

        let result = await api.create_stripe_customer(customerData);

        if (!result) {
          this.error.status = true;
          this.error.details = "Failed to create customer. Please try again.";
          this.loading = false;
          return;
        }
        this.$store.dispatch("setStripeCustomerDetails", result);
        this.showModal = true;
        this.error.status = false;
        this.error.details = null;
      } catch (error) {
        this.error.status = true;
        this.error.details = error.message || "Failed to create customer. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    async createSubscription(paymentMethodId) {
      let subscription = {
        customer_id: this.customerDetails.id,
        services: this.subscribedServices,
        payment_method_id: paymentMethodId,
      };
      let result = await api.create_stripe_subscription(subscription);
      if (result.error) {
        this.error.status = true;
        this.error.details = result.error.message;
        this.loading = false;
        return;
      }
      this.subSecret = result.client_secret;
      await this.$store.dispatch("getStripeCustomerDetails");
      await this.$store.dispatch("getUpcomingStripeInvoice");
      await this.confirmNewSub();
    },

    async confirmNewSub() {
      const { error, paymentIntent } = await this.stripe.confirmCardPayment(this.subSecret);

      if (error) {
        this.error.status = true;
        this.error.details = error.message || "Payment confirmation failed. Please try again.";
      } else if (paymentIntent.status === "succeeded") {
        this.confirmSubscription.status = true;
        this.error.status = false;
        this.error.details = null;
        this.tenantId = null;
      } else {
        this.error.status = true;
        this.error.details = "Payment was not completed. Please try again.";
      }
      this.loading = false;
    },
  },

  computed: {
    ...mapGetters({
      companyName: "companyName",
      companySettings: "companySettings",
      customerDetails: "stripeCustomerDetails",
      beamyProducts: "beamyProducts",
      siteMap: "siteMap",
      cogsEnabled: "cogsEnabled",
      upcomingInvoice: "stripeUpcomingInvoice",
    }),

    hasSubscription() {
      return !!(this.customerDetails && this.customerDetails.subscriptions && this.customerDetails.subscriptions.data && this.customerDetails.subscriptions.data.length > 0);
    },

    getStatusClass() {
      return (status) => {
        const statusClasses = {
          active: "status-active",
          trialing: "status-trialing",
          incomplete: "status-incomplete",
          incomplete_expired: "status-incomplete-expired",
          past_due: "status-past-due",
          canceled: "status-canceled",
          unpaid: "status-unpaid",
          paused: "status-paused",
        };

        return statusClasses[status] || "status-unknown";
      };
    },

    subscribedServices() {
      if (!this.beamyProducts || !this.beamyProducts.products) return [];
      const typeToCount = {
        pos: this.currentCounts.pos,
        kds: this.currentCounts.kds,
        site: this.currentCounts.sites,
        cogs: this.currentCounts.cogs,
      };
      let subscribed = this.beamyProducts.products
        .filter((p) => p.metadata && p.metadata.type && typeToCount[p.metadata.type] > 0)
        .map((p) => ({ product_id: p.id, price_id: p.price_id, quantity: typeToCount[p.metadata.type] }));

      return subscribed;
    },

    currentCounts() {
      const counts = {
        sites: this.siteMap.length || 0,
        pos: 0,
        kds: 0,
        cogs: this.cogsEnabled ? 1 : 0,
      };
      if (this.siteMap) {
        this.siteMap.forEach((site) => {
          if (site.stations) {
            counts.pos += site.stations.pos.length || 0;
            counts.kds += site.stations.kds.length || 0;
          }
        });
      }
      return counts;
    },

    areaCodePlaceholder() {
      return this.customerForm.address.country && this.countryCodes[this.customerForm.address.country] ? this.countryCodes[this.customerForm.address.country] : "+1";
    },

    phoneNumberPlaceholder() {
      const examples = {
        GB: "20 7946 0958",
        AU: "4 1234 5678",
        NZ: "21 123 4567",
      };

      return examples[this.customerForm.address.country] || "123 456 7890";
    },

    combinedPhoneNumber() {
      if (this.customerForm.phoneAreaCode && this.customerForm.phoneNumber) {
        return this.customerForm.phoneAreaCode + " " + this.customerForm.phoneNumber;
      }
      return "";
    },
  },

  watch: {
    "customerForm.address.country"(newCountry) {
      if (newCountry && this.countryCodes[newCountry]) {
        this.customerForm.phoneAreaCode = this.countryCodes[newCountry];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.wrapper {
  .loading {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--opacity-background);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .success-banner {
    background-color: #00b70fa8;
    color: white;
    padding: 1rem;
    border-radius: 10px;
    margin: 1rem auto;
    width: 90%;
    text-align: center;
  }

  .error {
    background-color: #ff4444;
    color: white;
    padding: 1rem;
    border-radius: 10px;
    margin: 1rem 0;
    width: 90%;
    text-align: center;
  }

  .pay-options {
    width: 75%;
    margin: 1rem auto;
    padding: 1rem;
    background-color: var(--primary-colour);
    box-shadow: var(--box-shadow);
    border-radius: 5px;
    .content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      margin: 0.5rem;
      width: 100%;
      .subtitle {
        color: var(--action-colour);
        h4 {
          margin: 1rem 0 !important;
        }
      }

      button {
        background-color: var(--action-colour);
        color: var(--secondary-text-colour);
        border: none;
        padding: 0.75rem 1.5rem;
        border-radius: 10px;
        box-shadow: var(--box-shadow);
        cursor: pointer;
        width: 95%;
        margin: 1rem 0;
        &:hover {
          opacity: 0.9;
        }
        &:active {
          scale: 0.95;
        }
      }

      input {
        border: none;
        border-radius: 5px;
        padding: 0.5rem;
        margin: 0.5rem 0;
        background-color: var(--secondary-colour);
        color: var(--text-colour);
        box-shadow: var(--box-shadow);
        width: 90%;

        &:focus {
          outline: none;
          border-color: var(--action-colour);
          box-shadow: var(--box-shadow);
        }
      }

      select {
        border: none;
        border-radius: 5px;
        padding: 0.5rem;
        margin: 0.5rem 0;
        background-color: var(--secondary-colour);
        color: var(--text-colour);
        box-shadow: var(--box-shadow);

        &:focus {
          outline: none;
          border-color: var(--action-colour);
          box-shadow: var(--box-shadow);
        }
      }

      label {
        color: var(--text-colour);
        font-weight: lighter;
        margin: 0.75rem 0 0.25rem 0;
      }

      .phone-input-row {
        display: flex;
        gap: 1.5rem;
        width: 95%;
        .area-code {
          width: 15%;
        }
        .phone-number {
          width: 90%;
        }
      }
    }
  }
  .sections-container {
    .details {
      header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 2rem 0;

        .reactivate {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0.5rem;

          span {
            color: var(--warning-colour);
            font-size: 0.9rem;
          }

          button {
            background-color: var(--action-colour);
            color: var(--secondary-text-colour);
            border: none;
            box-shadow: var(--box-shadow);
            cursor: pointer;
            padding: 0.5rem 1rem;
            border-radius: 5px;
            margin: 0;
            &:hover {
              opacity: 0.9;
            }
            &:active {
              scale: 0.95;
            }
          }
        }

        .how {
          font-size: small;
          color: var(--action-colour);
          text-decoration: underline;
          cursor: pointer;
        }
      }

      margin: 2rem;
      .status {
        display: flex;
        align-items: baseline;
        justify-content: space-evenly;
        gap: 2rem;
        background-color: var(--primary-colour);
        padding: 2rem;
        border-radius: 1rem;
        box-shadow: var(--box-shadow);
        h2 {
          color: var(--text-colour);
          font-size: 1.2rem;
          font-weight: 600;
        }

        .subscription-card {
          width: 100%;
          background: var(--secondary-colour);
          border-radius: 0.5rem;
          box-shadow: var(--box-shadow);
          border: 1px solid var(--bordeer-colour);

          .subscription-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.5rem;
            border-bottom: 1px solid var(--border-colour);

            .cancel {
              font-size: 0.9rem;
              color: red;
              font-weight: 600;
            }

            h3 {
              margin: 0;
              color: var(--text-colour);
              font-size: 1.1rem;
            }

            .status-badge {
              padding: 0.25rem 0.75rem;
              border-radius: 20px;
              font-size: 0.8rem;
              font-weight: 600;

              &.status-active {
                background: #00b70fa8;
                color: white;
              }

              &.status-trialing {
                background: #17a2b8;
                color: white;
              }

              &.status-incomplete,
              &.status-incomplete-expired {
                background: #ffc107;
                color: #212529;
              }

              &.status-past-due,
              &.status-unpaid {
                background: #dc3545;
                color: white;
              }

              &.status-canceled {
                background: #6c757d;
                color: white;
              }

              &.status-paused {
                background: #fd7e14;
                color: white;
              }

              &.status-default {
                background: #e9ecef;
                color: #495057;
              }
            }
          }

          .subscription-info {
            padding: 1.5rem;

            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 0.75rem;

              &:last-child {
                margin-bottom: 0;
              }

              .label {
                font-weight: 600;
                color: var(--text-colour-secondary);
              }

              .value {
                color: var(--text-colour);
              }
            }
          }

          .subscription-items {
            padding: 1.5rem;
            border-top: 1px solid var(--border-colour);

            h4 {
              margin: 0 0 1rem 0;
              color: var(--text-colour);
              font-size: 1rem;
            }

            .item-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 0.75rem;

              &:last-child {
                margin-bottom: 0;
              }

              .item-details {
                .item-name {
                  font-weight: 600;
                  color: var(--text-colour);
                  margin-bottom: 0.25rem;
                  text-transform: uppercase;
                }

                .item-quantity {
                  font-size: 0.9rem;
                  color: var(--text-colour-secondary);
                }
              }

              .item-price {
                text-align: right;
                color: var(--text-colour);

                .billing-interval {
                  font-size: 0.9rem;
                  color: var(--text-colour-secondary);
                }
              }
            }
          }

          .subscription-total {
            padding: 1.5rem;
            border-top: 1px solid var(--border-colour);
            background: rgba(var(--action-colour-rgb), 0.1);
            display: flex;
            justify-content: space-between;

            span {
              font-size: small;
              color: var(--action-colour);
              text-decoration: underline;
              cursor: pointer;
            }

            strong {
              color: var(--text-colour);
              font-size: 1.1rem;
            }
          }
        }

        .info-container {
          width: 100%;

          .actions {
            display: flex;
            justify-content: space-between;
            margin-top: 1rem;

            button {
              background-color: var(--action-colour);
              color: var(--secondary-text-colour);
              border: none;
              box-shadow: var(--box-shadow);
              cursor: pointer;
              margin: 0;

              &.danger {
                background-color: var(--danger-colour);
              }

              &:hover {
                opacity: 0.9;
              }
              &:active {
                scale: 0.95;
              }
            }
          }
          .customer-info {
            padding: 1.5rem;
            background: var(--secondary-colour);
            border-radius: 0.5rem;
            box-shadow: var(--box-shadow);
            border: 1px solid var(--secondary-colour);

            h3 {
              margin: 0 0 0.5rem 0;
            }

            .info-row {
              display: flex;
              margin-bottom: 0.5rem;

              &:last-child {
                margin-bottom: 0;
              }

              .label {
                font-weight: 600;
                color: var(--text-colour-secondary);
                margin-right: 0.5rem;
              }

              .value {
                color: var(--text-colour);
              }
            }
          }

          .payment-methods {
            margin-top: 1rem;
            padding: 1.5rem;
            background: var(--secondary-colour);
            border-radius: 0.5rem;
            box-shadow: var(--box-shadow);
            border: 1px solid var(--secondary-colour);

            .action {
              background-color: var(--action-colour);
              color: var(--secondary-text-colour);
              border: none;
              padding: 1rem;
              border-radius: 0.5rem;
              box-shadow: var(--box-shadow);
              cursor: pointer;
              margin: 0.5rem 0;
            }

            h3 {
              margin: 0 0 1rem 0;
              color: var(--text-colour);
            }

            .payment-card {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 1rem;
              background: var(--primary-colour);
              border-radius: 0.5rem;
              box-shadow: var(--box-shadow);
              border: 1px solid var(--border-colour);

              .card-info {
                flex: 1;

                .card-brand {
                  display: flex;
                  gap: 0.5rem;
                  margin-bottom: 0.5rem;

                  .brand-name {
                    font-weight: 700;
                    color: var(--action-colour);
                    text-transform: uppercase;
                  }

                  .card-type {
                    background: var(--action-colour);
                    color: var(--secondary-text-colour);
                    padding: 0.125rem 0.5rem;
                    border-radius: 12px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: uppercase;
                  }
                }

                .card-number {
                  font-family: monospace;
                  font-size: 1rem;
                  color: var(--text-colour);
                  margin-bottom: 0.5rem;
                }

                .card-details {
                  display: flex;
                  gap: 1rem;

                  .expiry,
                  .country {
                    font-size: 0.9rem;
                    color: var(--text-colour-secondary);
                  }

                  .country {
                    text-transform: uppercase;
                    font-weight: 600;
                  }
                }
              }

              .default-badge {
                background: var(--success-colour, #28a745);
                color: white;
                padding: 0.25rem 0.75rem;
                border-radius: 12px;
                font-size: 0.75rem;
                font-weight: 600;

                span {
                  letter-spacing: 0.5px;
                }
              }
            }

            .no-payment-methods {
              padding: 2rem;
              text-align: center;
              background: var(--primary-colour);
              border-radius: 0.5rem;
              border: 2px dashed var(--border-colour);

              p {
                margin: 0;
                color: var(--text-colour-secondary);
                font-style: italic;
              }
            }
          }
        }
      }

      .invoice-container {
        margin-top: 2rem;
        background: var(--primary-colour);
        border-radius: 1rem;
        box-shadow: var(--box-shadow);
        padding: 2rem;

        .upcoming-invoice {
          .invoice-header {
            margin-bottom: 1.5rem;

            .invoice-info {
              .info-row {
                display: flex;
                justify-content: space-between;
                margin-bottom: 0.75rem;

                &:last-child {
                  margin-bottom: 0;
                }

                .label {
                  font-weight: 600;
                  color: var(--text-colour-secondary);
                }

                .value {
                  color: var(--text-colour);
                }
              }
            }
          }

          .invoice-items {
            margin: 1.5rem 0;

            h4 {
              margin: 0 0 1rem 0;
              color: var(--text-colour);
              font-size: 1rem;
              border-bottom: 1px solid var(--border-colour);
              padding-bottom: 0.5rem;
            }

            .invoice-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 1rem;
              margin-bottom: 1rem;
              background-color: var(--secondary-colour);
              border-radius: 0.5rem;
              box-shadow: var(--box-shadow);
              padding: 1rem;

              .invoice-detail {
                span {
                  font-weight: 600;
                  color: var(--action-colour);
                }

                p {
                  margin: 0.25rem 0 0 0;
                  font-size: 0.9rem;
                  color: var(--text-colour-secondary);
                }
              }
            }
          }

          .invoice-totals {
            padding: 1rem;
            background: var(--secondary-colour);
            border-radius: 0.5rem;
            box-shadow: var(--box-shadow);

            .total-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 0.75rem;

              &:last-child {
                margin-bottom: 0;
              }

              &.final-total {
                font-size: 1.1rem;
                font-weight: 700;
              }

              .label {
                font-weight: 600;
                color: var(--text-colour-secondary);
              }

              .value {
                color: var(--text-colour);
                font-weight: 600;
              }
            }
          }
        }

        .no-invoice {
          background: var(--secondary-colour);
          border-radius: 8px;
          padding: 2rem;
          text-align: center;
          box-shadow: var(--box-shadow);

          p {
            margin: 0;
            color: var(--text-colour-secondary);
            font-style: italic;
          }
        }
      }
    }
  }
}

@media only screen and (orientation: portrait) {
  .wrapper {
    width: 90%;
    padding: 0.5rem;

    .pay-options {
      flex-direction: column;
      align-items: center;
      justify-content: center;

      .content {
        width: 100%;
        align-items: center;

        .subtitle {
          width: 90%;

          p {
            margin: 0.25rem;
          }
        }
      }
    }

    .details,
    .totals {
      width: 95%;
    }

    .sections-container {
      flex-direction: column;
      gap: 0.5rem;
    }
  }
}
</style>
